import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import type React from 'react';

import type {
  AuthPessoa,
  Escopo,
  PermissionCache,
} from '@/services/auth.service';
import type {
  CompleteRecoveryResult,
  RecoveryAbandonment,
  VerifyRecoveryResult,
} from '@/services/recuperarSenha.service';
import type { RecoveryRequestResult } from '@joshua/domain/recoveryErrors';

// ----------------------------------------------------------------------------
// Types used by AuthContext and its consumers. Extracted out of AuthContext.tsx
// to keep the context file focused on behavior (state, effects, callbacks).
// ----------------------------------------------------------------------------

export type { Escopo };

export interface AuthUser extends SupabaseUser {
  email: string;
}

export interface PessoaConfigHomeBg {
  tipo: 'preset' | 'cor' | 'imagem';
  valor: string;
  overlay_cor?: string;
  overlay_opacidade?: number;
  filtro?: 'none' | 'grayscale' | 'sepia';
}

export interface PessoaConfigNotificacoes {
  email: boolean;
  push: boolean;
}

export interface PessoaConfig {
  tema?: 'light' | 'dark';
  menu_pinado?: boolean;
  home_bg?: PessoaConfigHomeBg;
  notificacoes?: PessoaConfigNotificacoes;
}

export const DEFAULT_PESSOA_CONFIG: PessoaConfig = {
  tema: 'light',
  menu_pinado: false,
  home_bg: { tipo: 'preset', valor: 'default' },
  notificacoes: { email: true, push: false },
};

/**
 * Shape of pessoa held in AuthContext state. Extends the narrow AuthPessoa
 * service type with an index signature because downstream consumers still
 * read occasional fields we don't enumerate here. Tightening this is
 * follow-up work once the last .jsx consumers are converted.
 */
export interface AuthContextPessoa extends AuthPessoa {
  [key: string]: unknown;
}

export interface AuthPermission {
  id: string;
  codigo: string;
  nome: string;
  origem: 'perfil' | 'usuario' | 'fallback';
}

export type LoadingStage =
  | 'authenticating'
  | 'loading-profile'
  | 'loading-permissions'
  | 'validating'
  | 'complete'
  | null;

export interface AuditFields {
  tenant_id: string | null;
  filial_id: string | null;
  created_by: string | null;
  updated_by: string | null;
}

export interface SessionHealth {
  isHealthy: boolean;
  lastCheck: Date | null;
  tokenExpiry: Date | null;
  autoRefreshEnabled: boolean;
}

export interface AuthContextType {
  user: AuthUser | null;
  pessoa: AuthContextPessoa | null;
  permissions: AuthPermission[];
  loading: boolean;
  loadingStage: LoadingStage;
  signIn: (
    email: string,
    senha: string,
  ) => Promise<{ user: AuthUser; session: Session }>;
  signOut: () => Promise<void>;
  hasPermission: (recursoOuCodigo: string, acao?: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  getPermissionScope: (recurso: string, acao: string) => Escopo | null;
  /** JOSH-91: holds a view on the recurso, but only on the mobile channel. */
  temVisualizarEmOutroCanal: (recurso: string) => boolean;
  isAuthenticated: boolean;
  authError: string | null;
  securityStatus: 'secure' | 'fallback' | 'error' | 'inconsistent';
  refreshPermissions: () => Promise<void>;
  forceRefreshPermissions: () => Promise<void>;
  /** Reloads the permission cache without flipping the global loading flag. */
  silentRefreshPermissions: () => Promise<void>;
  validateAuthState: () => Promise<boolean>;
  sessionHealth: SessionHealth;
  permissionCache: PermissionCache;
  cacheTimestamp: number;
  tenantId: string | null;
  filialId: string | null;
  getAuditFields: () => AuditFields;
  pessoaConfig: PessoaConfig;
  updatePessoaConfig: (config: Partial<PessoaConfig>) => Promise<PessoaConfig>;
  homeBgPreview: PessoaConfigHomeBg | null;
  setHomeBgPreview: React.Dispatch<React.SetStateAction<PessoaConfigHomeBg | null>>;

  // JOSH-73: password recovery by emailed code.
  /** A verified recovery session in this tab awaits the new password. */
  recoveryPending: boolean;
  /**
   * Set when a recovery ended without a new password (abandonment, cold-start
   * cleanup, lost session): the request step opens with this email and message.
   */
  recoveryNotice: RecoveryAbandonment | null;
  dismissRecoveryNotice: () => void;
  requestRecoveryCode: (email: string) => Promise<RecoveryRequestResult>;
  verifyRecoveryCode: (email: string, code: string) => Promise<VerifyRecoveryResult>;
  completeRecovery: (password: string) => Promise<CompleteRecoveryResult>;
  abandonRecovery: () => Promise<RecoveryAbandonment | null>;
}
